import React, { useState, useMemo, useCallback, useEffect } from "react";
import { SearchProvider, useSearch } from "../components/common";
import { UserMenu } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import QueryAutocomplete from "../components/search/QueryAutocomplete";
import { useTranslation } from "react-i18next";
import "./HomeAlternative.css";

/* ─── SVG icons (inline to avoid extra deps) ─────────────────── */
const Icons = {
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
  ),
  star: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
  ),
  filter: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
  ),
  grid: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
  ),
  folder: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
  ),
  download: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
  ),
  export: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
  ),
  send: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
  ),
  tag: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
  ),
  trash: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
  ),
  back: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
  ),
  doc: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
  ),
  chevronDown: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
  ),
  compare: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="8" height="18" rx="1"/><rect x="14" y="3" width="8" height="18" rx="1"/></svg>
  ),
  menu: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
  ),
};

/* ─── Helper: case-insensitive getter ─────────────────────────── */
function getCI(obj, keys) {
  if (!obj) return undefined;
  const map = new Map(Object.keys(obj).map(k => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = map.get(String(k).toLowerCase());
    if (real !== undefined) return obj[real];
  }
  return undefined;
}

function fmtDate(v) {
  try {
    const dt = typeof v === "string" ? new Date(v) : v;
    if (!isNaN(dt)) {
      return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    return String(v);
  } catch { return String(v); }
}

function getExt(doc) {
  return (getCI(doc, ["fileExtension", "extension", "type"]) || "").toString().toUpperCase() || "DOC";
}

function getDocName(doc) {
  return doc?.originalName || doc?.fileName || doc?.documentName || doc?.name || "Untitled";
}

function getDocDate(doc) {
  return getCI(doc, ["documentDate", "date", "createdAt", "created"]);
}

function getPageCount(doc) {
  const p = Number(doc?.pageCount ?? doc?.pages);
  return isNaN(p) ? null : p;
}

/* ─── Main Page ───────────────────────────────────────────────── */
export default function HomeAlternative() {
  return (
    <SearchProvider>
      <HomeAlternativeInner />
    </SearchProvider>
  );
}

function HomeAlternativeInner() {
  const { t } = useTranslation();
  const {
    query, setQuery, runSearch, loading,
    rows, rawResults, dbs,
    selectedDoc, selectDocument,
    history, saved, removeSaved, clearHistory,
    toggleSavedFor, isSaved,
    interpreted,
    lastSearchExpression,
  } = useSearch();

  const [activeTab, setActiveTab] = useState("ai"); // 'ai' | 'structured' | 'filters'
  const [checkedDocs, setCheckedDocs] = useState(new Set());
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });

  // structured search form state
  const [formDb, setFormDb] = useState("");
  const [formAccount, setFormAccount] = useState("");
  const [formDateFrom, setFormDateFrom] = useState("");
  const [formDateTo, setFormDateTo] = useState("");
  const [formType, setFormType] = useState("PDF");

  // Group rows by database
  const groups = useMemo(() => {
    const map = new Map();
    for (const row of (rows || [])) {
      const db = row?.dbName || row?.database || "Unknown";
      if (!map.has(db)) map.set(db, []);
      map.get(db).push(row);
    }
    return map;
  }, [rows]);

  // Group items by date within selected db
  const dateGroups = useMemo(() => {
    const items = rows || [];
    const map = new Map();
    for (const row of items) {
      const d = getDocDate(row);
      const key = d ? fmtDate(d) : "Unknown Date";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }
    return map;
  }, [rows]);

  const totalResults = (rows || []).length;
  const totalPages = useMemo(() => {
    let sum = 0;
    for (const r of (rows || [])) {
      const p = getPageCount(r);
      if (p) sum += p;
    }
    return sum;
  }, [rows]);

  // active filters for sidebar — derived from the parsed query
  const activeFilters = useMemo(() => {
    const filters = [];
    if (formAccount) filters.push({ label: "Account", value: formAccount });
    if (formDateFrom) filters.push({ label: "Date", value: formDateFrom });

    const fmtVal = (v) => {
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      // Trim ISO date-times to YYYY-MM-DD when they look like dates.
      const m = s.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/);
      return m ? m[1] : s;
    };

    const opPrefix = {
      $eq: "",
      $gt: "> ",
      $gte: "≥ ",
      $lt: "< ",
      $lte: "≤ ",
      $like: "~ ",
      $regex: "/",
    };

    const walk = (node) => {
      if (!node || typeof node !== "object") return;
      for (const [key, val] of Object.entries(node)) {
        if ((key === "$and" || key === "$or") && Array.isArray(val)) {
          val.forEach(walk);
        } else if (val && typeof val === "object" && !Array.isArray(val)) {
          for (const [op, operand] of Object.entries(val)) {
            const display = fmtVal(operand).replace(/^%|%$/g, "");
            const pre = opPrefix[op] ?? `${op} `;
            filters.push({ label: key, value: `${pre}${display}` });
          }
        } else {
          filters.push({ label: key, value: fmtVal(val) });
        }
      }
    };

    const expr = (lastSearchExpression || interpreted || "").trim();
    if (expr) {
      try {
        walk(JSON.parse(expr.replace(/'/g, '"')));
      } catch {
        // Parse failed — leave the parsed-clause list empty rather than
        // dumping the raw query string as a single chip.
      }
    }
    return filters;
  }, [formAccount, formDateFrom, interpreted, lastSearchExpression]);

  const toggleCheck = useCallback((docKey) => {
    setCheckedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docKey)) next.delete(docKey);
      else next.add(docKey);
      return next;
    });
  }, []);

  const handleStructuredSearch = () => {
    let q = "";
    if (formAccount) q += `account:${formAccount} `;
    if (formDateFrom) q += `from:${formDateFrom} `;
    if (formDateTo) q += `to:${formDateTo} `;
    if (formType) q += `type:${formType} `;
    q = q.trim();
    if (q) {
      setQuery(q);
      runSearch(q);
    }
  };

  const handleReset = () => {
    setFormAccount("");
    setFormDateFrom("");
    setFormDateTo("");
    setFormType("PDF");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") runSearch(query);
  };

  const onRowHover = (row, e) => {
    setHoveredRow(row);
    setHoverPos({ x: e.clientX - 80, y: e.clientY - 260 });
  };

  // First DB name for display
  const firstDb = useMemo(() => {
    const entries = Array.from(groups.entries());
    return entries.length > 0 ? entries[0][0] : (dbs?.[0]?.dbName || "");
  }, [groups, dbs]);

  return (
    <div className="ha-root">
      {/* Nav strip */}
      <div className="nav-strip">
        <NavBar />
      </div>

      {/* Global search bar */}
      <div className="ha-global-search">
        <div className="ha-search-icon">{Icons.search}</div>
        <div className="ha-search-box">
          <QueryAutocomplete
            placeholder={t("search.placeholderAi") || "Search documents, customers, IBAN, invoices..."}
            onKeyDown={onKeyDown}
            disabled={loading}
          />
          <button className="ha-search-go" onClick={() => runSearch(query)} disabled={loading}>
            {loading ? "..." : "\u279C"}
          </button>
        </div>
        <button
          className="ha-ai-toggle active"
          title="AI Search"
        >
          AI
        </button>
        <div className="ha-user-slot">
          <UserMenu />
        </div>
      </div>

      {/* 3-panel layout */}
      <div className="ha-layout">
        {/* ── Left Sidebar ── */}
        <aside className="ha-sidebar">
          <div>
            <div className="ha-sidebar-title">CONTEXT</div>

            {/* Active Filters */}
            <div className="ha-sidebar-section">
              <div className="ha-sidebar-section-title">ACTIVE</div>
              {activeFilters.length > 0 ? (
                <>
                  {activeFilters.map((f, i) => (
                    <div key={i} className="ha-filter-tag">
                      <span className="label">{f.label}:</span>
                      <span className="value">{f.value}</span>
                    </div>
                  ))}
                  <button className="ha-clear-link" onClick={handleReset}>Clear Filters</button>
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#9ca3af", padding: "4px 0" }}>No active filters</div>
              )}
            </div>
          </div>

          {/* Recent Searches */}
          <div className="ha-sidebar-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div className="ha-sidebar-section-title">RECENT SEARCHES</div>
              {history.length > 0 && (
                <button className="ha-clear-link" style={{ fontSize: 11 }} onClick={clearHistory}>{t("common.clear")}</button>
              )}
            </div>
            {history.slice(0, 5).map((h, i) => (
              <button key={i} className="ha-recent-item" onClick={() => { setQuery(h.q); runSearch(h.q); }}>
                <span className="dot" />
                <span className="text">{h.q}</span>
              </button>
            ))}
            {history.length > 5 && (
              <button className="ha-view-all">View All</button>
            )}
          </div>

          {/* Saved Views */}
          <div className="ha-sidebar-section">
            <div className="ha-sidebar-section-title">SAVED VIEWS</div>
            {saved.length === 0 ? (
              <div style={{ fontSize: 12, color: "#9ca3af", padding: "4px 0" }}>No saved views</div>
            ) : (
              saved.slice(0, 5).map((s, i) => (
                <button key={i} className="ha-saved-item" onClick={() => { setQuery(s.q); runSearch(s.q); }} title={s.q}>
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }}>
                    <span style={{ fontWeight: s.name !== s.q ? 600 : 400 }}>{s.name}</span>
                    {s.name !== s.q && (
                      <span style={{ fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{s.q}</span>
                    )}
                  </span>
                </button>
              ))
            )}
          </div>

          {/* Workflows */}
          <div className="ha-sidebar-section">
            <div className="ha-sidebar-section-title">WORKFLOWS</div>
            <button className="ha-workflow-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
              <span className="wf-label">Customer Audit</span>
              <span className="wf-arrow">&rsaquo;</span>
            </button>
            <button className="ha-workflow-item">
              {Icons.search}
              <span className="wf-label">Index Search</span>
              <span className="wf-arrow">&rsaquo;</span>
            </button>
            <button className="ha-add-workflow">
              <span style={{ fontSize: 16 }}>+</span>
              New Workflow
            </button>
          </div>
        </aside>

        {/* ── Center Panel ── */}
        <div className="ha-center">
          {/* Tabs */}
          <div className="ha-tabs">
            <button
              className={`ha-tab${activeTab === "ai" ? " active" : ""}`}
              onClick={() => setActiveTab("ai")}
            >
              {Icons.star} AI Assist
            </button>
            <button
              className={`ha-tab${activeTab === "filters" ? " active" : ""}`}
              onClick={() => setActiveTab("filters")}
            >
              {Icons.filter} Filters
            </button>
            <button
              className={`ha-tab${activeTab === "structured" ? " active" : ""}`}
              onClick={() => setActiveTab("structured")}
            >
              {Icons.grid} Structured
            </button>
          </div>

          {/* Center body */}
          <div className="ha-center-body">
            {/* Structured Search Form (visible in structured tab) */}
            {activeTab === "structured" && (
              <div className="ha-structured-form">
                <h2>Structured Search</h2>
                <div className="subtitle">Query across multiple fields, databases, use and saved workflow.</div>
                <div className="ha-form-grid">
                  <div className="ha-form-row full">
                    <span className="ha-form-label">Database</span>
                    <select className="ha-form-select" value={formDb} onChange={e => setFormDb(e.target.value)}>
                      <option value="">Select database...</option>
                      {(dbs || []).map(db => (
                        <option key={db.dbName} value={db.dbName}>{db.dbName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="ha-form-row">
                    <span className="ha-form-label">Account No</span>
                    <input className="ha-form-input" value={formAccount} onChange={e => setFormAccount(e.target.value)} placeholder="Enter account number" />
                  </div>
                  <div className="ha-form-row">
                    <span className="ha-form-label">Type</span>
                    <select className="ha-form-select" value={formType} onChange={e => setFormType(e.target.value)}>
                      <option value="PDF">PDF</option>
                      <option value="HTML">HTML</option>
                      <option value="TXT">TXT</option>
                      <option value="">All</option>
                    </select>
                  </div>
                  <div className="ha-form-row">
                    <span className="ha-form-label">Date</span>
                    <input className="ha-form-input" type="text" value={formDateFrom} onChange={e => setFormDateFrom(e.target.value)} placeholder="From" />
                    <span style={{ color: "#9ca3af", fontSize: 13 }}>to</span>
                    <input className="ha-form-input" type="text" value={formDateTo} onChange={e => setFormDateTo(e.target.value)} placeholder="To" />
                  </div>
                </div>
                <div className="ha-form-actions">
                  <button className="ha-btn ha-btn-primary" onClick={handleStructuredSearch}>Search</button>
                  <button className="ha-btn ha-btn-secondary" onClick={handleReset}>Reset</button>
                  <button className="ha-btn ha-btn-secondary">Save as Workflow</button>
                </div>
              </div>
            )}

            {/* AI Assist header */}
            {activeTab === "ai" && totalResults > 0 && (
              <div className="ha-ai-header">
                <div className="db-selector">
                  {firstDb || "Results"} <span style={{ fontWeight: 400, color: "#6b7280" }}>&middot; {totalResults} documents</span>
                </div>
                <div className="sort-icons">
                  <button title="Sort">{Icons.chevronDown}</button>
                  <button title="Layout">{Icons.menu}</button>
                </div>
              </div>
            )}

            {/* Results Table */}
            {totalResults > 0 ? (
              <div className="ha-results-table-wrap">
                {Array.from(dateGroups.entries()).map(([dateLabel, items]) => (
                  <React.Fragment key={dateLabel}>
                    <div className="ha-date-group">
                      {Icons.folder}
                      <span className="date-label">{dateLabel}</span>
                      <span className="date-count">&middot; {items.length} documents</span>
                      <button className="date-chevron">{Icons.chevronDown}</button>
                    </div>
                    <table className="ha-table">
                      <thead>
                        <tr>
                          <th><span className="ha-check" /></th>
                          <th>NAME</th>
                          <th>DATE</th>
                          <th>PAGES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((row, idx) => {
                          const key = `${dateLabel}-${idx}`;
                          const ext = getExt(row).toLowerCase();
                          const name = getDocName(row);
                          const date = getDocDate(row);
                          const pages = getPageCount(row);
                          const isSelected = selectedDoc === row;
                          const isChecked = checkedDocs.has(key);
                          return (
                            <tr
                              key={key}
                              className={isSelected ? "selected" : ""}
                              onClick={() => selectDocument(row)}
                              onMouseEnter={e => onRowHover(row, e)}
                              onMouseLeave={() => setHoveredRow(null)}
                            >
                              <td>
                                <span
                                  className={`ha-check${isChecked ? " checked" : ""}`}
                                  onClick={e => { e.stopPropagation(); toggleCheck(key); }}
                                />
                              </td>
                              <td>
                                <div className="name-cell">
                                  <span className={`file-badge ${ext || "default"}`}>.{ext || "DOC"}</span>
                                  <span className="name-text">{name}</span>
                                </div>
                              </td>
                              <td className="date-col">{date ? fmtDate(date) : "-"}</td>
                              <td className="pages-col">{pages != null ? pages : "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              !loading && (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>{Icons.search}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No results yet</div>
                  <div style={{ fontSize: 13 }}>Try searching for documents using the search bar above or the structured search form.</div>
                </div>
              )
            )}
            {loading && (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Searching...</div>
              </div>
            )}
          </div>

          {/* Footer stats */}
          {totalResults > 0 && (
            <div className="ha-footer">
              <div className="ha-footer-stats">
                <span>Results: {totalResults}</span>
                {totalPages > 0 && <span>&middot; Pages: {totalPages}</span>}
                <span>&middot; Type: {totalResults} PDF</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Right Detail Panel ── */}
        <DetailPanel selectedDoc={selectedDoc} selectDocument={selectDocument} />

        {/* Batch Actions Bar */}
        <div className="ha-batch-bar">
          <span className="ha-batch-label">Batch Actions</span>
          <button className="ha-batch-btn">{Icons.download} Download</button>
          <button className="ha-batch-btn">{Icons.export} Export</button>
          <button className="ha-batch-btn">{Icons.send} Send</button>
          <button className="ha-batch-btn ai">{Icons.star} Run AI</button>
          <div className="ha-batch-sep" />
          <button className="ha-batch-btn">{Icons.tag} Tag</button>
        </div>
      </div>

      {/* Thumbnail preview popup */}
      {hoveredRow && (
        <div className="ha-thumb-popup" style={{ left: hoverPos.x, top: hoverPos.y }}>
          <div className="thumb-img">
            <div className="placeholder-doc">
              <div className="line" style={{ width: "90%", background: "#aaa" }} />
              <div className="line" style={{ width: "70%" }} />
              <div className="line" style={{ width: "85%" }} />
              <div className="line" style={{ width: "60%" }} />
              <div className="line" style={{ width: "75%", marginTop: 12 }} />
              <div className="line" style={{ width: "50%" }} />
            </div>
          </div>
          <div className="thumb-meta">
            {Icons.doc} {getPageCount(hoveredRow) || "?"} pages
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Right Detail Panel ──────────────────────────────────────── */
function DetailPanel({ selectedDoc, selectDocument }) {
  if (!selectedDoc) {
    return (
      <aside className="ha-detail">
        <div className="ha-detail-empty">
          <div>
            <div style={{ marginBottom: 8 }}>{Icons.doc}</div>
            <div>Select a document to view details</div>
          </div>
        </div>
      </aside>
    );
  }

  const name = getDocName(selectedDoc);
  const ext = getExt(selectedDoc);
  const date = getDocDate(selectedDoc);
  const pages = getPageCount(selectedDoc);
  const account = getCI(selectedDoc, ["archiveNo", "archiveno", "account", "customerNo", "customerNumber"]);
  const docType = getCI(selectedDoc, ["documentType", "type"]);

  // Split name at last dot for rendering
  const dotIdx = name.lastIndexOf(".");
  const baseName = dotIdx > 0 ? name.slice(0, dotIdx) : name;
  const extPart = dotIdx > 0 ? name.slice(dotIdx) : "";

  return (
    <aside className="ha-detail">
      {/* Back button */}
      <div className="ha-detail-back">
        <button onClick={() => selectDocument(null)}>{Icons.back}</button>
      </div>

      {/* Document header */}
      <div className="ha-detail-header">
        <div className="doc-name">
          {baseName}<span className="ext">{extPart}</span>
        </div>
        <div className="doc-date">{date ? fmtDate(date) : ""}</div>
      </div>

      {/* AI Summary */}
      <div className="ha-detail-section">
        <div className="ha-detail-section-title">
          <span className="icon ai">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5"/></svg>
          </span>
          AI Summary
        </div>
        <ul className="ha-ai-bullets">
          <li>Financial statement for {date ? fmtDate(date) : "N/A"}</li>
          {account && <li>Customer: {account}</li>}
          <li>No anomalies detected</li>
        </ul>
      </div>

      {/* Extracted Data */}
      <div className="ha-detail-section">
        <div className="ha-detail-section-title">
          <span className="icon data">{Icons.doc}</span>
          Extracted Data
        </div>
        <div className="ha-extracted-grid">
          {account && (
            <div className="ha-extracted-row">
              <span className="label">Account:</span>
              <span className="value">{account}</span>
            </div>
          )}
          <div className="ha-extracted-row">
            <span className="label">Type:</span>
            <span className="value">{docType || ext || "Document"}</span>
          </div>
          {pages != null && (
            <div className="ha-extracted-row">
              <span className="label">Pages:</span>
              <span className="value">{pages}</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="ha-detail-section">
        <div className="ha-detail-section-title">
          <span className="icon actions">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
          </span>
          ACTIONS
        </div>
        <div className="ha-actions-row">
          <button className="ha-action-btn primary">View</button>
          <button className="ha-action-btn">{Icons.download} Download</button>
          <button className="ha-action-btn">{Icons.compare} Compare</button>
          <button className="ha-action-more">{Icons.menu}</button>
        </div>
      </div>

      {/* Related */}
      <div className="ha-detail-section">
        <div className="ha-detail-section-title">
          <span className="icon related">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          </span>
          RELATED
        </div>
        <div className="ha-related-list">
          <button className="ha-related-item">
            <span className="rel-icon">{Icons.doc}</span>
            Previous statement (Dec 2013)
          </button>
          <button className="ha-related-item">
            <span className="rel-icon">{Icons.doc}</span>
            Invoice set ({date ? fmtDate(date) : "N/A"})
          </button>
        </div>
      </div>
    </aside>
  );
}
